import { css } from '@emotion/react'
import { Button } from 'components/button/Button'
import { Icon } from 'components/icon'
import {
  SAspectRatio,
  SChooseAnimationModal,
  SColumn,
  SGrid,
  SHeading,
  SText,
  STitle,
} from './ChooseAnimationModal.styled'
import { AnimationDriver } from 'components/memory/MemoryUtils'
import { ModalConfig } from 'components/modal/Modal'
import {
  PromisingModalProps,
  usePromisingModal,
} from 'components/modal/promisingModal/PromisingModal'
import { Radio } from 'components/radio/Radio'
import { BlockSpinner } from 'components/spinner/Spinner'
import { Text } from 'components/Typography'
import { AutoPlayer } from 'components/videoPlayer/autoPlayer/AutoPlayer'
import { useForm } from 'react-hook-form'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'
import { BREAKPOINTS, pxToRem } from 'utils/styled'

interface Fields {
  driver: AnimationDriver
}

const modalConfig: ModalConfig = {
  modalCss: css`
    @media ${BREAKPOINTS.MD.min} {
      max-width: ${pxToRem(445)}rem;
    }
  `,
}

export const ChooseAnimationModal = (props: PromisingModalProps<AnimationDriver>) => {
  const { t, isLoading } = useDynamicTranslations('life_stories')

  const form = useForm<Fields>({
    defaultValues: {
      driver: 'FUN',
    },
  })

  const onSubmit = async (data: Fields) => {
    props.onResolve?.(data.driver)
    props.close()
  }

  if (isLoading) return <BlockSpinner />
  return (
    <SChooseAnimationModal>
      <SHeading>
        <Icon name="StarWand" color="black" />
        <STitle>{t('life_stories.inside_page.choose_animation_modal.title')}</STitle>
      </SHeading>
      <SText>{t('life_stories.inside_page.choose_animation_modal.desc')}</SText>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <SGrid>
          <SColumn>
            <SAspectRatio ratio={1}>
              <AutoPlayer src="/animation/fun.mp4" />
            </SAspectRatio>
            <Radio {...form.register('driver')} value="FUN">
              <Text>{t('life_stories.inside_page.choose_animation_modal.fun_label')}</Text>
            </Radio>
          </SColumn>
          <SColumn>
            <SAspectRatio ratio={1}>
              <AutoPlayer src="/animation/nostalgia.mp4" />
            </SAspectRatio>
            <Radio {...form.register('driver')} value="NOSTALGIA">
              <Text>{t('life_stories.inside_page.choose_animation_modal.nostalgic_label')}</Text>
            </Radio>
          </SColumn>
        </SGrid>

        <Button
          loading={form.formState.isSubmitting}
          size="large"
          block
          data-testid="continue-button"
          type="submit"
        >
          {t('life_stories.inside_page.choose_animation_modal.action_button')}
        </Button>
      </form>
    </SChooseAnimationModal>
  )
}

export const useChooseAnimationModal = () => {
  const [modal, openModalAndPromise, closeModal] = usePromisingModal<AnimationDriver>(
    ({ close, onResolve }) => <ChooseAnimationModal close={close} onResolve={onResolve} />,
    modalConfig
  )

  return [modal, openModalAndPromise, closeModal] as const
}
